import React, { useState, useEffect, useRef, useMemo } from 'react';
import heroMainVideo from '../assets/videos/main_video.mp4';
import { getVideoUrl } from '../utils/mediaUtils';

function HeroSection({ data }) {
  // Get video fields from data
  const { taglinePrefix = '', taglineAccent = '', philosophy = '', video_url = '', video_file = '' } = data || {};
  
  // Memoize video URL to prevent unnecessary recalculations
  const videoSource = useMemo(() => {
    const rawVideo = video_file || video_url;
    // PRIORITY 1: Use database video from content_published
    if (rawVideo && typeof rawVideo === 'string' && rawVideo.trim().length > 0) {
      const resolved = getVideoUrl(rawVideo.trim());
      console.log('[HeroSection] Using resolved video URL:', resolved);
      return resolved || rawVideo;
    }
    
    // PRIORITY 2: Fall back to local video file
    console.log('[HeroSection] Using local video fallback');
    return heroMainVideo;
  }, [video_url, video_file]);
  
  if (!data) {
    console.warn('[HeroSection] No data provided');
    return null;
  }

  // Split tagline into individual words for equal spacing
  const prefixWords = (taglinePrefix && typeof taglinePrefix === 'string') 
    ? taglinePrefix.split(' ').filter(w => w)
    : [];
  
  return (
    <section className="hero-section hero-fullscreen">
      <div className="hero-video-bg" aria-hidden="true">
        <VideoPlayer src={videoSource} />
      </div>
      <div className="hero-overlay" aria-hidden="true" />
      <div className="hero-content hero-content-layout">
        <article className="hero-message-center">
          <p className="hero-tagline">
            {prefixWords.map((word, idx) => (
              <span key={idx} className="hero-tagline-word">{word}</span>
            ))}
            <span className="hero-tagline-accent">{taglineAccent}</span>
          </p>
          <p className="hero-philosophy">
            {philosophy}
          </p>
        </article>
      </div>
    </section>
  );
}

// Separate component so useEffect/useRef work correctly with full autoplay compliance
function VideoPlayer({ src }) {
  const videoRef = useRef(null);
  const [currentSrc, setCurrentSrc] = useState(src);

  // Keep currentSrc in sync when src prop changes
  useEffect(() => {
    setCurrentSrc(src);
  }, [src]);

  // Check if src is YouTube URL
  const isYouTube = typeof currentSrc === 'string' && (currentSrc.includes('youtube.com') || currentSrc.includes('youtu.be'));

  const handleVideoError = () => {
    console.warn('[HeroSection] Video error for src:', currentSrc, '- falling back to default local video');
    if (currentSrc !== heroMainVideo) {
      setCurrentSrc(heroMainVideo);
    }
  };

  useEffect(() => {
    if (isYouTube) return;
    const video = videoRef.current;
    if (!video || !currentSrc) return;

    // Critical for browser autoplay policies (Chrome, Safari, Edge, Mobile)
    video.defaultMuted = true;
    video.muted = true;
    video.playsInline = true;

    const playVideo = () => {
      if (video.paused) {
        const promise = video.play();
        if (promise !== undefined) {
          promise.catch(err => {
            console.warn('[HeroSection] Autoplay initial attempt prevented:', err.message);
          });
        }
      }
    };

    video.addEventListener('loadeddata', playVideo);
    video.addEventListener('canplay', playVideo);

    playVideo();

    // Fallback on first user interaction if browser policy strictly requires gesture
    const handleInteraction = () => {
      if (video && video.paused) {
        video.play().catch(() => {});
      }
    };

    window.addEventListener('click', handleInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', handleInteraction, { once: true, passive: true });
    window.addEventListener('scroll', handleInteraction, { once: true, passive: true });

    return () => {
      video.removeEventListener('loadeddata', playVideo);
      video.removeEventListener('canplay', playVideo);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
    };
  }, [currentSrc, isYouTube]);

  if (isYouTube) {
    let videoId = '';
    if (currentSrc.includes('v=')) {
      videoId = currentSrc.split('v=')[1]?.split('&')[0];
    } else if (currentSrc.includes('youtu.be/')) {
      videoId = currentSrc.split('youtu.be/')[1]?.split('?')[0];
    } else if (currentSrc.includes('embed/')) {
      videoId = currentSrc.split('embed/')[1]?.split('?')[0];
    }
    return (
      <iframe
        className="hero-video-frame"
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1&enablejsapi=1`}
        title="Hero Background Video"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <video
      ref={videoRef}
      src={currentSrc}
      className="hero-video-element"
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      onError={handleVideoError}
    />
  );
}

export default HeroSection;
